import React, { Dispatch, FC, SetStateAction, useState } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
  Button,
  Pagination,
  Select,
  Stack,
  MenuItem,
  InputLabel,
  TextField,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { MoreIcon, SearchIcon } from "assets/icons";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "shared/lib/hooks/hooks";
import SecondConfirmModal from "shared/ui/SecondConfirmModal/SecondConfirmModal";
import { useAppDispatch } from "shared/config/configureStore";
import trashImg from "assets/images/admin/trash_black.svg";
import { deleteProductCategoryByCategoryId } from "api/thunk/productCategories/deleteActions/deleteProductCategoryByCategoryId";
import { getProductsCategories } from "api/thunk/productCategories/getActions/getProductsCategories";
import { useNavigate } from "react-router-dom";
import editIcon from "assets/images/admin/editVoucher.svg";
import { UPDATE_PRODUCT_CATEGORY } from "pages/paths";
import {
  Input,
} from "entities/admin/lib/MUIStyles/MUIStyles";
import { setProductsCategoriesPage, setProductsCategoriesPageSize } from "api/slice/productCategoriesSlice/productCategoriesSlice";
interface IProductsCategoriesTabProps {
  searchCategories: string;
  setSearchCategories: Dispatch<SetStateAction<string>>;
}

const ProductsCategoriesTab: FC<IProductsCategoriesTabProps> = ({
  searchCategories,
  setSearchCategories
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: productCategories, loading: productCategoriesLoading } = useAppSelector(
    (state) => state.productsCategories.productsCategories.response
  );
  const { page: productCategoriesPage, pageSize: productCategoriesPageSize } = useAppSelector(
    (state) => state.productsCategories.productsCategories.params
  );

  const page = productCategoriesPage ?? 1;
  const pageSize = productCategoriesPageSize ?? 10;

  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProductCategoryId, setSelectedCategoryProductId] = useState<number | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>, selectedProductCategoryId: number) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPopoverPosition({ top: rect.bottom, left: rect.right });
    setPopoverOpen(true);
    setSelectedCategoryProductId(selectedProductCategoryId)
  };

  const handlePopoverClose = () => {
    setPopoverOpen(false);
    setPopoverPosition(null);
  };


  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
    handlePopoverClose();
  };

  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ECF2F7",
      color: "#3E444A",
      padding: "8px 16px",
      fontFamily: "Raleway",
      fontSize: "12px",
      lineHeight: "20px",
      border: "none",
      fontWeight: "700 !important",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(odd)": {
      backgroundColor: "#F5FAFF",
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const handleConfirmDelete = async () => {
    if (selectedProductCategoryId !== null) {
      try {
        const response = await dispatch(
          deleteProductCategoryByCategoryId(selectedProductCategoryId)
        );

        if (response.meta.requestStatus === "fulfilled") {
          dispatch(getProductsCategories({}));
        }
      } catch (error) {
        console.error(error);
      }
    }

    setDeleteModalOpen(false);
    setSelectedCategoryProductId(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    dispatch(setProductsCategoriesPage(value))
  }

  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    const newSize = Number(e.target.value);

    dispatch(setProductsCategoriesPageSize(+e.target.value));
    dispatch(setProductsCategoriesPage(1));
    dispatch(getProductsCategories({ page: 1, pageSize: newSize }));
  };

  return (
    <Box
      sx={{
        margin: "0 auto",
        width: "100%",
        background: "#FFFFFF",
        borderRadius: "16px",
        marginTop: "24px",
        padding: "24px",
      }}
    >
      <Box>
        <TextField
          type="search"
          variant="outlined"
          size="small"
          sx={{
            ...Input,
            minWidth: '500px',
            width: "50%",
            marginBottom: "24px",
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#0090D1',
              },
            },
          }}
          placeholder={"Поиск"}
          value={searchCategories}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearchCategories(e.target.value)}
        />
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell sx={{ fontWeight: 600, width: "50px" }}>№</StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 600 }}>{t("tables.name")}</StyledTableCell>
                <StyledTableCell sx={{ border: "none", fontSize: "14px" }} align="left">
                  {t("myMarathon.actions")}
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {productCategoriesLoading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : productCategories?.items?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      py={4}
                      color="#9e9e9e"
                      fontSize="14px"
                    >
                      No data
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                productCategories?.items?.map((category, index: number) => {
                  return (
                    <StyledTableRow key={category.id}>
                      <StyledTableCell>{(page - 1) * pageSize + index + 1}</StyledTableCell>
                      <StyledTableCell>{category.name}</StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 48, p: 0 }}>
                        <Button
                          size="small"
                          onClick={(e) => handlePopoverOpen(e, category?.id)}
                        >
                          <MoreIcon />
                        </Button>
                      </StyledTableCell>
                    </StyledTableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          spacing={2}
          sx={{
            margin: "30px auto 20px auto",
            width: "500px",
            alignItems: "center",
            "@media (max-width: 515px)": {
              width: "400px",
            },
            "@media (max-width: 420px)": {
              width: "300px",
            },
          }}
        >
          <Pagination
            count={Math.ceil((productCategories?.totalCount || 0) / pageSize)}
            page={productCategoriesPage ?? 1}
            onChange={handlePageChange}
            color="primary"
            size="medium"
            variant="outlined"
            shape="rounded"
            sx={{ margin: "0 auto" }}
          />
          <>
            <Select
              value={productCategoriesPageSize}
              name="pageSize"
              onChange={handlePageSizeChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "12px",
                    padding: "0 10px",
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "#e3f2fd",
                    },
                  },
                },
              }}
              sx={{
                borderRadius: "12px",
                border: "1px solid #C0D4E3",
                width: "100px",
                margin: "0 auto",
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                ".MuiSelect-select": {
                  padding: "10px 32px 10px 12px",
                  color: "#3E444A",
                  fontFamily: "Raleway",
                  fontSize: "14px",
                  lineHeight: "24px",
                },
              }}
            >
              {[10, 20, 30, 40, 50].map((value) => (
                <MenuItem
                  key={value}
                  value={value}
                  sx={{ borderRadius: "10px" }}
                >
                  {value}
                </MenuItem>
              ))}
            </Select>
            <InputLabel>{t("activeMarathonListOfParticipants.quantity")}</InputLabel>
          </>
        </Stack>
      </Box>
      {
        popoverPosition && (
          <Popover
            open={popoverOpen}
            onClose={handlePopoverClose}
            anchorReference="anchorPosition"
            anchorPosition={{
              top: popoverPosition.top,
              left: popoverPosition.left,
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                borderRadius: "12px",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
                minWidth: 160,
                p: "4px",
              },
            }}
          >
            <Box display="flex" flexDirection="column">
              <Button
                fullWidth
                size="small"
                onClick={() =>
                  navigate(
                    `/admin_panel/${UPDATE_PRODUCT_CATEGORY(selectedProductCategoryId)}`
                  )
                }
                startIcon={
                  <Box
                    component="img"
                    src={editIcon}
                    alt="Edit"
                    sx={{
                      width: 18,
                      height: 18,
                      filter:
                        "invert(39%) sepia(99%) saturate(749%) hue-rotate(166deg) brightness(95%) contrast(101%)",
                    }}
                  />
                }
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                  color: "#0090D1",
                  "&:hover": {
                    backgroundColor: "#F4F6F8",
                  },
                }}
              >
                {t("buttons.edit")}
              </Button>

              <Button
                fullWidth
                size="small"
                onClick={handleDeleteClick}
                startIcon={
                  <Box
                    component="img"
                    src={trashImg}
                    alt="Delete"
                    sx={{
                      width: 18,
                      height: 18,
                      filter:
                        "invert(53%) sepia(92%) saturate(3125%) hue-rotate(336deg) brightness(104%) contrast(97%)",
                    }}
                  />
                }
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                  color: "#FF6960",
                  "&:hover": {
                    backgroundColor: "rgba(255, 105, 96, 0.08)",
                  },
                }}
              >
                {t("buttons.delete")}
              </Button>
            </Box>
          </Popover>
        )
      }
      <SecondConfirmModal
        open={deleteModalOpen}
        status="error"
        title={t("confirmModal.deleteProductCategoryConfirmation")}
        description={t("confirmModal.deleteProductCategoryText")}
        confirmText={t("buttons.delete")}
        cancelText={t("buttons.cancel")}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box >
  );
};

export default ProductsCategoriesTab;